import SocketContext from 'context/SocketContext';
import { Timing } from 'lib/util';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { actions, useAppDispatch, useSelectAuthentication, useSelectConversations } from 'store';
import { Message } from 'types';

interface Props {
	children: ReactNode;
}

const API_HOST = process.env.REACT_APP_API_URL!;

const SocketManager = ({ children }: Props) => {
	const dispatch = useAppDispatch();
	const { isAuthenticated, accessToken, expiryAt } = useSelectAuthentication();
	const conversations = useSelectConversations();

	const [socket, setSocket] = useState<Socket | null>(null);
	const socketRef = useRef<Socket | null>(null);
	const joinedRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!isAuthenticated || accessToken === '' || Timing.isExpired(expiryAt)) {
			if (socketRef.current) {
				socketRef.current.removeAllListeners();
				socketRef.current.disconnect();
				socketRef.current.close();
				socketRef.current = null;
				setSocket(null);
			}
			joinedRef.current.clear();
			return;
		}

		if (socketRef.current?.connected) {
			return;
		}

		const newSocket = io(API_HOST, {
			reconnection: true,
			reconnectionAttempts: Infinity,
			reconnectionDelay: 2000,
			transports: ['websocket'],
			query: {
				Authorization: accessToken,
			},
		});

		newSocket.on('connect', () => {
			console.log(`SocketState: Connected to the server at ${API_HOST}`);
			// Force re-join after reconnect; previous room membership is gone.
			joinedRef.current.clear();
			for (const conversation of conversations.list) {
				const id = String(conversation.id);
				newSocket.emit('user:register', id);
				joinedRef.current.add(id);
			}
		});

		newSocket.on('connect_error', (error: Error) => {
			console.error('SocketState: connect_error', error);
		});

		newSocket.on('disconnect', () => {
			console.log('SocketState: Disconnected from the server.');
			joinedRef.current.clear();
		});

		socketRef.current = newSocket;
		setSocket(newSocket);

		return () => {
			newSocket.removeAllListeners();
			newSocket.disconnect();
			newSocket.close();
			if (socketRef.current === newSocket) {
				socketRef.current = null;
			}
			setSocket(null);
			joinedRef.current.clear();
		};
		// conversations.list intentionally omitted — rooms re-registered in a dedicated effect.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, accessToken, expiryAt]);

	// Stable message listeners (do not nest inside connect — avoids lost/stacked handlers).
	useEffect(() => {
		if (!socket) {
			return;
		}

		const onFinished = (temporaryId: string, message: Message) => {
			console.log('conversation:message:finished', temporaryId, message);
			dispatch(actions.messageUpdated({ temporaryId, message }));
		};
		const onReceived = (message: Message) => {
			console.log('conversation:message:received', message);
			dispatch(actions.messageReceived(message));
		};

		socket.on('conversation:message:finished', onFinished);
		socket.on('conversation:message:received', onReceived);

		return () => {
			socket.off('conversation:message:finished', onFinished);
			socket.off('conversation:message:received', onReceived);
		};
	}, [socket, dispatch]);

	// Join any new conversation rooms. Never un-register on list refresh — that
	// was racing with inbound messages and dropping live updates.
	useEffect(() => {
		if (!socket?.connected) {
			return;
		}

		for (const conversation of conversations.list) {
			const id = String(conversation.id);
			if (joinedRef.current.has(id)) {
				continue;
			}
			socket.emit('user:register', id);
			joinedRef.current.add(id);
		}
	}, [conversations.updatedAt, conversations.list, socket]);

	return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};

export default SocketManager;
